/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import { useState } from 'react';
import { first, last } from 'lodash';

import Icon from '@zapier/common-components/src/components/Icon';
import { Colors, Border } from '@zapier/theme';
import { StaticImage } from '@zapier/common-components/src/components/StaticAsset';
import { getInitialsFromString } from '@zapier/common-utils';

export type AvatarProps = {
  /** Name of user */
  name: string;
  /** Size in pixels */
  size: number;
  /** URL to image */
  url?: string;
};

const getNameInitials = (name: string): string => {
  const initials = getInitialsFromString(name.trim());
  const firstLetter = first(initials);

  if (!firstLetter) {
    return '';
  }

  return initials.length === 1
    ? firstLetter
    : `${firstLetter}${last(initials)}`;
};

const Styles = {
  root: (size: number) => css`
    align-items: center;
    background: ${Colors.adamantium};
    border-radius: ${Border.radius.default};
    display: inline-flex;
    height: ${size}px;
    width: ${size}px;
    justify-content: center;
    overflow: hidden;
    position: relative;
  `,
  image: (size: number) => css`
    border-radius: inherit;
    display: block;
    height: ${size}px;
    width: ${size}px;
    position: relative;
  `,
  initials: (size: number) => css`
    color: ${Colors.black};
    font-size: ${Math.floor(size * 0.45)}px;
    font-weight: 600;
    text-transform: uppercase;
  `,
};

/**
 * Displays a user's avatar.
 */
const Avatar = (props: AvatarProps) => {
  // Used to fall back to initials if the image fails to load
  const [hasError, setHasError] = useState(false);

  const { name, url, size } = props;
  const initials = name ? getNameInitials(name) : null;

  const showImage = url && !hasError;

  return (
    <div css={Styles.root(size)}>
      {showImage ? (
        <StaticImage
          alt={name}
          css={Styles.image(size)}
          onError={() => setHasError(true)}
          src={url || ''}
        />
      ) : initials ? (
        <span css={Styles.initials(size)}>{initials}</span>
      ) : (
        <Icon color="batmobile" name="user" size={size * 0.75} />
      )}
    </div>
  );
};

Avatar.defaultProps = {
  size: 36,
};

export default Avatar;
